<script lang="ts" setup>
    const props = defineProps<{
        isReversed?: boolean,
        sliderId?: string,
        slides: {
          description: string,
          title: string,
          link: {
            url: string,
            title: string,
          },
          image: {
            node: {
              mediaItemUrl: string,
              srcSet: string,
              uri: string,
              title: string
            }
          }
        }[]
    }>();

    const slides = props?.slides;
    const sliderId = props?.sliderId === undefined ? 'vertical-slider' : props?.sliderId;
    const leftArrowId = 'left-arrow-' + sliderId;
    const rightArrowId = 'right-arrow-'+ sliderId;
    const leftArrowMobileId = 'left-arrow-' + sliderId + '-mobile';
    const rightArrowMobileId = 'right-arrow-' + sliderId + '-mobile';
    const sliderContentId = 'slider-content-' + sliderId;
    const currentSlideNumberRef = ref<Number>(1);

onMounted(() => {
    if (slides.length > 1) {
        var	imgHero = document.getElementById(sliderId),
            imgHeroDots = Array.prototype.slice.call(document.getElementById("imgHero-dots")?.children),
            imgHeroContent = Array.prototype.slice.call(document.getElementById(sliderContentId)?.children),
            imgHeroLeftArrow = document.getElementById(leftArrowId),
            imgHeroRightArrow = document.getElementById(rightArrowId),
            imgHeroLeftArrowMobile = document.getElementById(leftArrowMobileId),
            imgHeroRightArrowMobile = document.getElementById(rightArrowMobileId),
            imgHeroSpeed = 4500,
            currentSlide = 0,
            currentActive = 0,
            imgHeroTimer : any;

        // Calculate and set uniform height for all slides
        function setUniformHeight() {
            // Only apply uniform height on desktop (768px and above)
            const isDesktop = window.innerWidth >= 768;

            if (!isDesktop) {
                // On mobile, just restore original structure without setting height
                imgHeroContent.forEach((slide: any, index: number) => {
                    slide.style.height = '';
                    slide.style.position = '';
                    slide.style.opacity = '';

                    // Set initial state - first slide active, others inactive
                    if (index === 0) {
                        slide.classList.add("active");
                    } else {
                        slide.classList.remove("active");
                    }
                });
                return;
            }

            let maxHeight = 0;

            // Temporarily remove classes and positioning to measure natural height
            imgHeroContent.forEach((slide: any) => {
                slide.classList.remove("active", "inactive");
                slide.style.position = 'static';
                slide.style.opacity = '1';
            });

            // Find the maximum height
            imgHeroContent.forEach((slide: any) => {
                const slideHeight = slide.offsetHeight;
                if (slideHeight > maxHeight) {
                    maxHeight = slideHeight;
                }
            });

            // Apply the maximum height and restore original structure
            imgHeroContent.forEach((slide: any, index: number) => {
                slide.style.height = maxHeight + 'px';
                slide.style.position = '';
                slide.style.opacity = '';

                // Set initial state - first slide active, others inactive
                if (index === 0) {
                    slide.classList.add("active");
                } else {
                    slide.classList.remove("active");
                }
            });
        }

        // Set uniform height after a short delay to ensure content is rendered
        setTimeout(setUniformHeight, 100);

        // Handle window resize to re-calculate heights
        window.addEventListener('resize', () => {
            setTimeout(setUniformHeight, 100);
        });
        function playSlide(slide : any) {
            for (var k = 0; k < slides.length; k++) {
                imgHeroContent[k].classList.remove("active");
                imgHeroContent[k].classList.remove("inactive");
            }

            if (slide < 0) {
                slide = currentSlide = imgHeroContent.length-1;
            }

            if (slide > imgHeroContent.length - 1) {
                slide = currentSlide = 0;
            }

            if (currentActive != currentSlide) {
                imgHeroContent[currentActive].classList.add("inactive");            
            }
            imgHeroContent[slide].classList.add("active");

            currentActive = currentSlide;
            currentSlideNumberRef.value = currentActive+1;
        }

        imgHeroLeftArrow?.addEventListener("click", function() {
            playSlide(currentSlide -= 1);
        })

        imgHeroRightArrow?.addEventListener("click", function() {
            playSlide(currentSlide += 1);
        })    

        imgHeroLeftArrowMobile?.addEventListener("click", function() {
            playSlide(currentSlide -= 1);
        })

        imgHeroRightArrowMobile?.addEventListener("click", function() {
            playSlide(currentSlide += 1);
        })    

        for (var l = 0; l < imgHeroDots.length; l++) {
            imgHeroDots[l].addEventListener("click", function() {
                playSlide(currentSlide = imgHeroDots.indexOf(this));
            })
        }
    }
});
</script>
<template>
    <div class="container-fluid d-flex align-items-center overflow-hidden">
        <div v-if="!props?.isReversed" class="pager d-md-flex d-none align-items-center col-md-1 pe-5">
            <span v-if="slides?.length > 1" :id="leftArrowId" :class="`${props?.isReversed ? 'reversed' : ''} arrow left`">
                <NuxtImg loading="lazy"
                    alt="Previous"
                    quality="80"
                    height="25" width="25"
                    src="/up-arrow.svg"/>
            </span>
            <h5 v-if="slides?.length > 1" :class="`${props?.isReversed ? 'reversed' : ''} text-green-light mb-0 page-number`">
                {{ currentSlideNumberRef }}/{{ slides?.length }}
            </h5>
            <span v-if="slides?.length > 1" :id="rightArrowId" :class="`${props?.isReversed ? 'reversed' : ''} arrow right`">
                <NuxtImg loading="lazy"
                    alt="Next"
                    quality="80"
                    height="25" width="25"
                    src="/down-arrow.svg"/>
            </span>
        </div>
        <div :id="sliderId" class="imgHero col-md-11">
            <div :class="`${props?.isReversed ? 'reversed rounded-end' : 'rounded-start'} wrap bg-green-light`">
                <template v-if="props?.isReversed">
                    <div class="h-100 start-0 z-n1 bg-green-light d-none position-absolute filler rounded-end"></div>
                </template>
                <template v-else>
                    <div class="h-100 end-0 z-n1 bg-green-light d-none position-absolute filler rounded-start"></div>
                </template>
                <ul v-if="slides?.length > 1" id="imgHero-dots" class="dots d-none">
                    <li class="dot active"></li>
                    <li class="dot"></li>
                    <li class="dot"></li>
                </ul>
                <div :id="sliderContentId" class="cont px-0">
                    <template v-for="(slide,index) in slides">
                        <div v-if="props?.isReversed" :class="`${index == 0 ? 'active' : ''} d-flex flex-column flex-md-row rounded-end py-md-2 pt-4 pb-0 pe-2 wrapper reversed`">
                            <div class="py-3 my-lg-5 py-lg-5 col-12 col-md-7 text-white d-flex flex-column justify-content-between pe-lg-5 text-start">
                                <div class="w-100 mb-5">
                                    <h1 class="mb-5 text-white" v-html="slide?.title"></h1>
                                    <div class="description w-75 z-2 position-relative" v-html="slide?.description">
                                    </div>
                                </div>
                                <div class="w-100">
                                    <a :href="slide?.link?.url" class="position-relative z-2 btn btn-secondary rounded-pill text-uppercase w-50 px-5 py-2">
                                    {{ slide?.link?.title }}
                                    </a>
                                </div>
                            </div>       
                            <div class="img reversed col-md-5 p-4 pe-3 pe-md-4 ps-0 ps-md-4 rounded-end">
                                <NuxtImg v-if="slide?.image?.node?.mediaItemUrl" :alt="slide?.image?.node?.title" class="h-100 w-100 object-fit-cover rounded img-hero" loading="lazy"
                                    :src="slide?.image?.node?.mediaItemUrl" format="webp" :srcset="slide?.image?.node?.srcSet" :quality="80"/>
                            </div>         
                        </div>
                        <div v-else :class="`${index == 0 ? 'active' : ''} d-flex flex-column-reverse flex-md-row rounded-start py-md-2 pt-4 pb-0 ps-md-2 ps-0 wrapper`">
                            <div class="img col-md-5 p-4 pe-3 p-md-4 col-12 rounded-start">
                                <NuxtImg v-if="slide?.image?.node?.mediaItemUrl" :alt="slide?.image?.node?.title" class="h-100 w-100 object-fit-cover rounded img-hero" loading="lazy"
                                    :src="slide?.image?.node?.mediaItemUrl" format="webp" :srcset="slide?.image?.node?.srcSet" :quality="80"/>
                            </div>  
                            <div class="py-3 my-lg-5 py-lg-5 col-12 col-md-7 text-white d-flex flex-column justify-content-between ps-lg-7 text-start">
                                <div class="w-100 mb-5">
                                    <h1 class="mb-5 text-white" v-html="slide?.title"></h1>
                                    <div class="description z-2 position-relative" v-html="slide?.description">
                                    </div>
                                </div>
                                <div class="w-100">
                                    <a :href="slide?.link?.url" class="position-relative z-2 btn btn-secondary rounded-pill text-uppercase w-50 px-5 py-2">
                                    {{ slide?.link?.title }}
                                    </a>
                                </div>
                            </div>       
                        </div>
                    </template>
                </div>
                <div class="pager-mobile d-md-none d-flex align-items-center col-md-1 justify-content-end pe-4 pb-4">
                    <span v-if="slides?.length > 1" :id="`${leftArrowId}-mobile`" :class="`${props?.isReversed ? 'reversed' : ''} arrow left pe-4`">
                        <NuxtImg loading="lazy"
                            alt="Previous"
                            quality="80"
                            height="20" width="15"
                            src="/left-white-arrow.svg"/>
                    </span>
                    <h6 v-if="slides?.length > 1" :class="`${props?.isReversed ? 'reversed' : ''} text-white mb-0 page-number pe-4`">
                        {{ currentSlideNumberRef }}/{{ slides?.length }}
                    </h6>
                    <span v-if="slides?.length > 1" :id="`${rightArrowId}-mobile`" :class="`${props?.isReversed ? 'reversed' : ''} arrow right`">
                        <NuxtImg loading="lazy"
                            alt="Next"
                            quality="80"
                            height="20" width="15"
                            src="/right-white-arrow.svg"/>
                    </span>
                </div>
            </div>
        </div>
        <div v-if="props?.isReversed" class="pager reversed d-md-flex d-none align-items-center col-md-1 ps-5">
            <span v-if="slides?.length > 1" :id="leftArrowId" :class="`${props?.isReversed ? 'reversed' : ''} arrow left`">
                <NuxtImg loading="lazy"
                    alt="Previous"
                    quality="80"
                    height="25" width="25"
                    src="/up-arrow.svg"/>
            </span>
            <h5 v-if="slides?.length > 1" :class="`${props?.isReversed ? 'reversed' : ''} text-green-light mb-0 page-number`">
                {{ currentSlideNumberRef }}/{{ slides?.length }}
            </h5>
            <span v-if="slides?.length > 1" :id="rightArrowId" :class="`${props?.isReversed ? 'reversed' : ''} arrow right`">
                <NuxtImg loading="lazy"
                    alt="Next"
                    quality="80"
                    height="25" width="25"
                    src="/down-arrow.svg"/>
            </span>
        </div>
    </div>
</template>